import React, { useState, useEffect } from 'react';
import { IoClose, IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import { BsGithub } from "react-icons/bs";
import { FaGlobe } from "react-icons/fa";

// Renders text with **bold** segments as <strong>
const renderWithBold = (text, paragraphIndex) => {
  if (!text || typeof text !== 'string') return text;
  const parts = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;
  let keyIndex = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={`p${paragraphIndex}-${keyIndex++}`}>{text.slice(lastIndex, match.index)}</span>);
    }
    parts.push(<strong key={`p${paragraphIndex}-${keyIndex++}`}>{match[1]}</strong>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(<span key={`p${paragraphIndex}-${keyIndex}`}>{text.slice(lastIndex)}</span>);
  }
  return parts.length ? parts : text;
};

const ProjectModal = ({ isOpen, onClose, project }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Reset image index when project changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [project]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const { title, shortDescription, detailedDescription, images, skills, githubUrl, liveUrl, technologies } = project;

  const goToNext = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center mt-10 justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-gradient-to-r from-bodyColor to-[#202327] rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto relative no-scrollbar">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white z-10 bg-black bg-opacity-50 rounded-full p-2 transition-colors duration-300"
        >
          <IoClose size={24} />
        </button>

        {/* Image Gallery Section */}
        <div className="relative">
          <div className="w-full h-64 md:h-80 lg:h-96 overflow-hidden rounded-t-lg relative">
            <img
              src={images[currentImageIndex]}
              alt={`${title} screenshot ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full p-2 transition-all duration-300"
                >
                  <IoChevronBackOutline size={24} />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full p-2 transition-all duration-300"
                >
                  <IoChevronForwardOutline size={24} />
                </button>
              </>
            )}

            {/* Image Counter */}
            {images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-3 py-1 rounded-full text-sm">
                {currentImageIndex + 1} / {images.length}
              </div>
            )}
          </div>

          {/* Thumbnail Navigation */}
          {images.length > 1 && (
            <div className="flex justify-center gap-2 p-4 bg-black bg-opacity-25">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-16 h-12 rounded overflow-hidden border-2 transition-all duration-300 ${
                    index === currentImageIndex 
                      ? 'border-designColor' 
                      : 'border-transparent hover:border-gray-400'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 md:mb-0">
              {title}
            </h2>
            <div className="flex gap-3">
              {githubUrl && (
                <a
                  href={githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg w-12 h-12 rounded-full bg-black inline-flex justify-center items-center text-gray-400 hover:text-designColor transition-colors duration-300"
                >
                  <BsGithub />
                </a>
              )}
              {liveUrl && (
                <a
                  href={liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg w-12 h-12 rounded-full bg-black inline-flex justify-center items-center text-gray-400 hover:text-designColor transition-colors duration-300"
                >
                  <FaGlobe />
                </a>
              )}
            </div>
          </div>

          {/* Short Description */}
          <p className="text-gray-300 text-lg mb-6 leading-relaxed">
            {shortDescription}
          </p>

          {/* Detailed Description */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-designColor mb-4">Project Details</h3>
            <div className="text-gray-300 leading-relaxed">
              {detailedDescription.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-3">
                  {renderWithBold(paragraph, index)}
                </p>
              ))}
            </div>
          </div>

          {/* Technologies Used */}
          {technologies && technologies.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-designColor mb-4">Technologies Used</h3>
              <div className="flex flex-wrap gap-2">
                {technologies.map((tech, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-designColor bg-opacity-20 text-designColor rounded-full text-sm font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Skills/Features */}
          {skills && skills.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-designColor mb-4">Key Features & Skills</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {skills.map((skill, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-designColor rounded-full flex-shrink-0"></div>
                    <span className="text-gray-300">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;